import { useState } from "react";
import { api } from "../api";
import { BackButton } from "../components/BackButton";
import { ConfirmDialog } from "../components/Dialog";
import { useOnline } from "../hooks";
import type { LibraryView } from "../library";
import { navigate } from "../router";

const when = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

export function Trash({ view, refresh }: { view: LibraryView | null; refresh(): void }) {
  const online = useOnline();
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { folders, documents } = view?.trashTopLevel() ?? { folders: [], documents: [] };
  const count = folders.length + documents.length;

  async function restore(fn: () => Promise<unknown>) {
    setMessage(null);
    try {
      await fn();
    } catch {
      setMessage("Couldn't restore. Are you online?");
    }
    refresh();
  }

  async function empty() {
    setConfirming(false);
    setBusy(true);
    setMessage(null);
    try {
      const r = await api.emptyTrash();
      setMessage(
        r.kept
          ? `Emptied. ${r.kept} recently changed item${r.kept === 1 ? " stays" : "s stay"} until the next backup.`
          : "Trash emptied.",
      );
    } catch {
      setMessage("Trash wasn't emptied because the backup didn't complete. Nothing was removed.");
    } finally {
      setBusy(false);
      refresh();
    }
  }

  return (
    <main className="library">
      <header className="library-header">
        <BackButton label="Library" onClick={() => navigate("/")} />
      </header>
      <h1>Trash</h1>
      <p className="hint">Items here stay in your backups until you empty the Trash.</p>
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}

      <ul className="item-list">
        {folders.map((f) => (
          <li key={f.id} className="item folder">
            <span className="item-main static">
              <span className="item-icon" aria-hidden>
                ▸
              </span>
              <span className="item-label">
                {f.name}
                <small>Folder · trashed {when(f.deletedAt)}</small>
              </span>
            </span>
            <button className="item-action" disabled={!online} onClick={() => restore(() => api.restoreFolder(f.id))}>
              Restore
            </button>
          </li>
        ))}
        {documents.map((d) => (
          <li key={d.id} className="item doc">
            <button className="item-main" onClick={() => navigate(`/d/${d.id}`)}>
              <span className="item-icon" aria-hidden />
              <span className="item-label">
                {d.title}
                <small>Trashed {when(d.deletedAt)}</small>
              </span>
            </button>
            <button className="item-action" disabled={!online} onClick={() => restore(() => api.restoreDocument(d.id))}>
              Restore
            </button>
          </li>
        ))}
      </ul>
      {view && count === 0 && <p className="hint">Trash is empty.</p>}

      {count > 0 && (
        <button className="danger" disabled={!online || busy} onClick={() => setConfirming(true)}>
          {busy ? "Backing up, then emptying…" : "Empty Trash"}
        </button>
      )}
      {confirming && (
        <ConfirmDialog
          title="Empty Trash?"
          message={
            <p>
              {count} item{count === 1 ? "" : "s"} will be permanently removed from Quill. A backup runs first, and
              the backup history keeps a copy.
            </p>
          }
          confirm="Empty Trash"
          danger
          onConfirm={empty}
          onClose={() => setConfirming(false)}
        />
      )}
    </main>
  );
}
